import { Box, Container, Paper, Typography } from "@mui/material";
import { DataTable } from "modules/common";
import { useState } from "react";
import ServicePointDialog from "./components/ServicePointDialog";
import { useCreateServicePointMutation, useDeleteServicePointMutation, useGetServicePointsQuery, useLazyGetServicePointsFromQoyodQuery, useUpdateServicePointMutation } from "api/adminSlice";
import { useTranslation } from "react-i18next";
import { cloneDeep, set } from "lodash";
import { Add } from "@mui/icons-material";
import moment from "moment";
import { LoadingButton } from "@mui/lab";

const $COLUMN_WIDTH = 200
export default function AdminServicePoints() {
  const { t } = useTranslation()

  const [page, setPage] = useState(1)

  //=================Service point API hooks==================
  const [getServicePointsFromQoyod,
    { isLoading: qoyodIsLoading, }//isSuccess: qoyodIsSuccess, error: qoyodIsError 
  ] = useLazyGetServicePointsFromQoyodQuery()

  const {
    data: servicePoints, isLoading: servicePointsLoading, isFetching: servicePointsFetching, refetch: refetchServicePoints
  } = useGetServicePointsQuery({ page, limit: 100 })

  const [createServicePoint,
    {
      isLoading: createServicePointLoading,
      isSuccess: createServicePointIsSuccess,
      error: createServicePointError,
      reset: createReset
    }
  ] = useCreateServicePointMutation()

  const [updateServicePoint,
    {
      isLoading: updateServicePointLoading,
      isSuccess: updateServicePointIsSuccess,
      error: updateServicePointError,
      reset: updateReset
    }
  ] = useUpdateServicePointMutation()

  const [deleteServicePoint,
    {
      isLoading: deleteServicePointLoading,
      isSuccess: deleteServicePointIsSuccess,
      error: deleteServicePointError,
      reset: deleteReset
    }
  ] = useDeleteServicePointMutation()

  //==========Get and navigate through service points==========
  const pageChangeHandler = ({ page }) => {
    if (!servicePointsFetching || !servicePointsLoading) {
      setPage(page)
    }
  }

  //===============Handle service point values change==============
  const [selectedServicePoint, setSelectedServicePoint] = useState({
    name: '',
    operationType: "",
    address: {
      area: '',
      description: '',
      coords: {}
    },
    workhours: {
      0: [null, null],
      1: [null, null],
      2: [null, null],
      3: [null, null],
      4: [null, null],
      5: [null, null],
      6: [null, null],
    }
  })

  const resetSelectedServicePoint = () => {
    setSelectedServicePoint({
      name: '',
      operationType: "",
      address: {
        area: '',
        description: '',
        coords: {}
      },
      workhours: {
        0: [null, null],
        1: [null, null],
        2: [null, null],
        3: [null, null],
        4: [null, null],
        5: [null, null],
        6: [null, null],
      }
    })
  }

  const handleChange = ({ target: { value, name, day, label } }) => {
    if (name === "workhours") {
      let payload = cloneDeep(selectedServicePoint)
      payload.workhours[day][label === "from" ? 0 : 1] = value
      setSelectedServicePoint(payload)
    } else {
      setSelectedServicePoint((prevState) => {
        let payload = cloneDeep(prevState)
        payload = set(payload, name, value)
        return payload
      })
    }
  }

  //==============Sercice point dialog handlers==============
  const [isEditing, setIsEditing] = useState(false)

  const [openServicePointDialog, setOpenServicePointDialog] = useState(false)

  const resetAPIHooks = () => {
    createReset()
    updateReset()
    deleteReset()
  }

  // const openDialogForCreateServicePoint = () => {
  //   resetAPIHooks()
  //   setIsEditing(false)
  //   setOpenServicePointDialog(true)
  // }

  const openDialogForUpdateServicePoint = (data) => {
    resetAPIHooks()
    data = cloneDeep(data)
    Object.keys(data?.workhours).forEach(day => {
      if (!!data.workhours[day]) {
        let from = data?.workhours[day][0].split(':')
        data.workhours[day][0] = moment().set({ hour: from[0], minute: from[1], second: from[2] })
        let to = data?.workhours[day][1].split(':')
        data.workhours[day][1] = moment().set({ hour: to[0], minute: to[1], second: to[2] })
      } else {
        data.workhours[day] = [null, null]
      }
    })
    setSelectedServicePoint(data)
    setIsEditing(true)
    setOpenServicePointDialog(true)
  }



  const closeDialogHandler = () => {
    setOpenServicePointDialog(false)
    if (isEditing) {
      resetSelectedServicePoint()
    }
    setIsEditing(false)
  }

  //============Set time for whole week==============================
  const onSetWholeWeek = (value) => {
    setSelectedServicePoint((prevState) => {
      let payload = cloneDeep(prevState)
      Object.keys(payload.workhours).forEach(dayIndex => {
        payload.workhours[dayIndex] = [...value]
      })
      return payload
    })
  }


  //================Service point CRUD handlers===============
  const createServicePointHandler = async (payload) => {
    let response = await createServicePoint(payload).unwrap()
    checkServicePointActionSuccess(response)
  }
  const updateServicePointHandler = async (payload) => {
    let response = await updateServicePoint({ id: selectedServicePoint?.id, payload }).unwrap()
    checkServicePointActionSuccess(response)
  }
  const deleteServicePointHandler = async (id) => {
    let response = await deleteServicePoint(id).unwrap()
    checkServicePointActionSuccess(response)
  }
  const checkServicePointActionSuccess = (response) => {
    if (response?.success) {
      refetchServicePoints()
      setTimeout(() => {
        setOpenServicePointDialog(false)
        resetSelectedServicePoint()
      }, 3000);
    }
  }

  const getServicePointsFromQoyodHandler = async () => {
    try {
      await getServicePointsFromQoyod()
      refetchServicePoints()
    } catch (error) { }
  }

  const columns = [
    { field: "name", headerName: t("Full Name"), width: $COLUMN_WIDTH * 1.5, },
    { field: "address.area", headerName: t("Area"), valueGetter: ({ row: { address } }) => address.area, width: $COLUMN_WIDTH * 1.5 },
    { field: "address.description", headerName: t("Description"), valueGetter: ({ row: { address } }) => address.description, width: $COLUMN_WIDTH * 1.5, },
  ];

  return (
    <Container>
      <Box display="flex" justifyContent="space-between" alignItems="center" my={25}>
        <Typography variant='h6'>{t("Service points")}</Typography>
        {/* <SearchTextFiled
          fullWidth
          label={t("Search")}
          size="small"
          value={searchKey ?? ''}
          onChange={onSearchKey}
        /> */}
        {/* <Button
          startIcon={<Add />}
          sx={{ textTransform: "none" }}
          variant="contained"
          onClick={openDialogForCreateServicePoint}
        >
          {t("Add")}
        </Button> */}
        <LoadingButton
          startIcon={<Add />}
          sx={{ textTransform: "none" }}
          variant="contained"
          loading={qoyodIsLoading}
          onClick={getServicePointsFromQoyodHandler}
        >
          {t("Import from Qoyod")}
        </LoadingButton>
      </Box>
      <Paper sx={{ height: "80vh" }}>
        <DataTable
          onPaginationModelChange={pageChangeHandler}
          paginationModel={{ page: page - 1, pageSize: 100 }}
          rows={servicePoints?.docs ?? []}
          columns={columns}
          rowCount={servicePoints?.totalDocs ?? 0}
          loading={servicePointsLoading || servicePointsFetching}
          onRowClick={({ row }) => openDialogForUpdateServicePoint(row)}
        />
      </Paper>
      <ServicePointDialog
        openServicePointDialog={openServicePointDialog}
        loading={createServicePointLoading || updateServicePointLoading || deleteServicePointLoading}
        isEditing={isEditing}
        servicePoint={selectedServicePoint}
        handleChange={handleChange}
        onSetWholeWeek={onSetWholeWeek}
        onCreateServicePoint={createServicePointHandler}
        onUpdateServicePoint={updateServicePointHandler}
        onDeleteServicePoint={deleteServicePointHandler}
        onCloseDialog={closeDialogHandler}
        error={createServicePointError || updateServicePointError || deleteServicePointError}
        isSuccess={createServicePointIsSuccess || updateServicePointIsSuccess || deleteServicePointIsSuccess}
      />
    </Container>
  )
}

{/* <Grid container justifyContent="space-between" alignItems="center" sx={{ my: 25 }} spacing={10}>
  <Grid item md={4} sm={4} xs={4}>
    <Typography variant='h6'>{t("Service points")}</Typography>
  </Grid>
  <Grid item md={4} sm={4} xs={12} order={{ xs: 3 }}>
    <SearchTextFiled
      fullWidth
      label={t("Search")}
      size="small"
      value={searchKey ?? ''}
      onChange={onSearchKeyChange}
    />
  </Grid>
  <Grid item md={2} sm={3} xs={4} order={{ xs: 2 }}>
    <Box display="flex" justifyContent="end">
      <Button
        startIcon={<Add />}
        sx={{ textTransform: "none" }}
        variant="contained"
        onClick={openDialogForCreateServicePoint}
      >
        {t("Add")}
      </Button>
    </Box>
  </Grid>
</Grid>

//=============Search by text handlers=============
const timerRef = useRef(null)
const [searchKey, setSearchKey] = useState('')
const onSearchKeyChange = ({ target: { value } }) => {
  setSearchKey(value)
  clearTimeout(timerRef.current)
  timerRef.current = setTimeout(() => {
    setParams((prevParams) => ({ ...prevParams, searchKey: value }))
  }, 1000);
} */}